import _ from 'lodash';
import { debounceTime, fromEvent, Subscription, tap } from 'rxjs';

import App from './app';
import { ComponentType } from '../types/app-types';
import { fromJQueryEvent } from '../utils/rxjs-utils';

abstract class Component<
  Selectors extends Record<string, string> = {},
  C extends HTMLElement = HTMLElement,
> {
  private _hasLoaded = false;

  private _listeners: Subscription[] = [];

  protected _isEnabled = true;

  protected _selectors: Selectors;

  public readonly type: ComponentType | 'class';

  public readonly container: C;

  public readonly key: string;

  constructor(type: ComponentType | 'class', container: C) {
    this.type = type;
    this.container = container;
    this.key = _.uniqueId(`${type}-`);
    // Perform component activation on first page load
    window.addEventListener('load', this._enter.bind(this));
    // Perform component activation/deactivation on page navigation
    const visibilityChangeHandler = this._handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', visibilityChangeHandler);
  }

  public enable() {
    this._isEnabled = true;
    this.container.classList.remove('disabled');
  }

  public disable() {
    this._isEnabled = false;
    this.container.classList.add('disabled');
  }

  private _handleVisibilityChange() {
    if (document.visibilityState === 'visible') this._enter();
    if (document.visibilityState === 'hidden') this._exit();
  }

  protected _addSubscription(subscription: Subscription) {
    this._listeners.push(subscription);
  }

  protected _addListener<K extends keyof globalThis.WindowEventMap | string>(
    target: HTMLElement[] | HTMLElement | Window | keyof Selectors | globalThis.JQuery<Element>,
    type: K,
    listener: (
      event: K extends keyof globalThis.WindowEventMap ? globalThis.WindowEventMap[K] : Event,
    ) => any,
    debounceTimeMs: number | null = null,
    preventDefault = true,
  ) {
    let elements = _.flatten([target]) as HTMLElement[];
    if (_.isString(target)) elements = Array.from(this._getChildren(target));

    const event =
      target instanceof jQuery
        ? fromJQueryEvent(target as globalThis.JQuery<Element>, type)
        : fromEvent(elements, type);

    const preventDefaultOperator = tap((e: Event) => {
      if (preventDefault) e.preventDefault();
    });

    const observable = _.isNumber(debounceTimeMs)
      ? event.pipe(preventDefaultOperator, debounceTime(debounceTimeMs))
      : event.pipe(preventDefaultOperator);

    const subscription = observable.subscribe(e => {
      if (this._isEnabled) listener.bind(this)(e as any);
    });

    this._listeners.push(subscription);
  }

  private _enter() {
    if (this._hasLoaded) return;
    this._hasLoaded = true;
    this._onEnter();
  }

  private _exit() {
    if (!this._hasLoaded) return;
    this._hasLoaded = false;
    this._listeners.forEach(listener => listener.unsubscribe());
    this._onExit();
  }

  protected _onEnter() {}

  protected _onExit() {}

  protected _getChild<T extends HTMLElement = HTMLElement>(selector: keyof Selectors) {
    return this.container.querySelector<T>(this._selectors[selector])!;
  }

  protected _getChildren<T extends HTMLElement = HTMLElement>(selector: keyof Selectors) {
    return this.container.querySelectorAll<T>(this._selectors[selector]);
  }

  protected _getChildComponent<T extends Component = Component>(type: ComponentType): T {
    const components = App.getComponentsOfType<T>(type);

    return components.find(component => this.container.contains(component.container));
  }

  protected _destroy() {
    this.container.remove();
    App.removeComponent(this);
  }
}

export default Component;
